import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button, Card, Eyebrow } from "@/components/studio/primitives";

export function billingLabel(billing) {
  if (billing.review) return "Billing needs administrator review";
  if (billing.status === "makeup") return "Covered by a makeup credit — no additional charge";
  if (billing.invoice_status) return `Monthly invoice ${billing.invoice_number}: ${billing.invoice_status}`;
  if (billing.monthly_billing) return "Included in monthly billing — invoice not issued yet";
  if (billing.status === "paid") return "Paid";
  if (billing.status === "refunded") return "Refunded";
  return billing.due_cents > billing.paid_cents ? "Payment outstanding" : "No payment due";
}

export default function LessonWorkspaceStatus({ connectionId, appointmentId, onRevision }) {
  const [data, setData] = useState(null), [error, setError] = useState(""), [saving, setSaving] = useState(false);
  const generation = useRef(0), [reload, setReload] = useState(0);
  const path = connectionId && appointmentId && !appointmentId.startsWith("cs_")
    ? `/scheduler/connections/${encodeURIComponent(connectionId)}/appointments/${encodeURIComponent(appointmentId)}/workspace` : null;
  useEffect(() => {
    const requestGeneration = ++generation.current;
    setData(null); setError(""); setSaving(false);
    if (!path) return undefined;
    const controller = new AbortController();
    Promise.resolve().then(() => api.get(path, { signal: controller.signal })).then(r => {
      if (!r?.data?.billing) throw new Error("Lesson details unavailable");
      if (!controller.signal.aborted) setData(r.data);
    }).catch(() => {
      if (!controller.signal.aborted) setError("Attendance and billing could not be loaded. Your notes are unchanged.");
    });
    return () => { generation.current = requestGeneration + 1; controller.abort(); };
  }, [path, reload]);
  if (!path) return null;
  const update = async attendance => {
    const current = generation.current;
    setSaving(true); setError("");
    try {
      const { data: next } = await api.put(path, { attendance, expected_revision: data.revision });
      if (current === generation.current) { setData(next); onRevision?.(next.revision); }
    } catch { if (current === generation.current) setError("The lesson may have changed. Reload its attendance before saving again."); }
    finally { if (current === generation.current) setSaving(false); }
  };
  return <Card className="my-6" data-testid="lesson-workspace-status">
    <Eyebrow>Attendance &amp; billing</Eyebrow>
    {error && <p role="alert" className="mt-3 text-sm text-[var(--state-danger)]">{error}</p>}
    {error && <Button className="mt-3" variant="ghost" onPress={() => setReload(value => value + 1)}>Reload lesson details</Button>}
    {!data && !error && <p role="status" className="mt-3 text-sm text-[var(--text-secondary)]">Loading lesson details…</p>}
    {data && <>
      <p className="mt-3 font-sans text-sm text-[var(--text-primary)]">{billingLabel(data.billing)}</p>
      <p className="mt-2 text-sm text-[var(--text-secondary)]">{new Intl.NumberFormat(undefined, { style: "currency", currency: data.billing.currency }).format(data.billing.paid_cents / 100)} paid of {new Intl.NumberFormat(undefined, { style: "currency", currency: data.billing.currency }).format(data.billing.due_cents / 100)}</p>
      <fieldset disabled={saving || !data.attendance_available} className="mt-5">
        <legend className="mb-3 text-sm text-[var(--text-secondary)]">Attendance: {data.attendance === "unmarked" ? "Not marked" : data.attendance}</legend>
        <div className="flex flex-wrap gap-2">
          {[['attended', 'Attended'], ['missed', 'Missed'], ['unmarked', 'Clear']].map(([value, label]) => <Button key={value} variant={data.attendance === value ? "primary" : "ghost"} onPress={() => update(value)} aria-pressed={data.attendance === value}>{label}</Button>)}
        </div>
      </fieldset>
      <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">{data.attendance_available ? "Attendance follows the billing policy. It does not refund a payment or create a makeup credit." : "Attendance becomes available when a confirmed private lesson starts."}</p>
    </>}
  </Card>;
}
